import React from 'react';
import { Calendar, AlertTriangle, Phone } from 'lucide-react';

const CancellationPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-950 to-black py-20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
            Cancellation <span className="text-yellow-400">Policy</span>
          </h1>
          <p className="text-gray-400 text-lg">Important information about booking cancellations</p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* How to Cancel */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">How to Cancel Your Booking</h2>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Contact Us</h3>
                    <p>Call us at 8506865393 or email crew.indyabungy@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Provide Booking Details</h3>
                    <p>Share your booking ID, name, and scheduled jump date</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Receive Confirmation</h3>
                    <p>You'll receive a cancellation confirmation and refund details via email</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Cancellation Fees */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Calendar className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Cancellation Fees Structure</h2>
                  <p className="text-gray-300">Fees depend on how far in advance you cancel:</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="py-3 px-4 text-white font-bold">Cancellation Period</th>
                      <th className="py-3 px-4 text-white font-bold">Cancellation Fee</th>
                      <th className="py-3 px-4 text-white font-bold">Refund Amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800">
                      <td className="py-3 px-4">15+ days before jump</td>
                      <td className="py-3 px-4">
                        <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-bold">0%</span>
                      </td>
                      <td className="py-3 px-4 text-green-400 font-bold">100%</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-3 px-4">8-14 days before jump</td>
                      <td className="py-3 px-4">
                        <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-bold">25%</span>
                      </td>
                      <td className="py-3 px-4 text-yellow-400 font-bold">75%</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-3 px-4">Less than 8 days</td>
                      <td className="py-3 px-4">
                        <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold">50%</span>
                      </td>
                      <td className="py-3 px-4 text-red-400 font-bold">50%</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">No-show / Same day</td>
                      <td className="py-3 px-4">
                        <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold">100%</span>
                      </td>
                      <td className="py-3 px-4 text-red-400 font-bold">0%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Rescheduling */}
            <section className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Rescheduling Option</h2>
              <p className="text-gray-300 mb-4">
                Instead of cancelling, you can <strong className="text-white">reschedule your jump once for free</strong> if you notify us at least 48 hours in advance. Additional rescheduling requests may incur a fee.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>First reschedule: <strong className="text-white">Free</strong> (48+ hours notice)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Additional reschedules: <strong className="text-white">₹500</strong> per change</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Subject to availability at new date/time</span>
                </li>
              </ul>
            </section>

            {/* Force Majeure */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Cancellation by Indya Bungy</h2>
                </div>
              </div>
              <p className="text-gray-300 mb-4">We may cancel your jump in the following situations:</p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Weather Conditions:</strong> Unsafe weather (high winds, rain, storms)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Technical Issues:</strong> Equipment maintenance or safety concerns</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Force Majeure:</strong> Natural disasters, government orders, or emergencies</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 font-bold mb-2">Full Refund Guaranteed</p>
                <p className="text-gray-300 text-sm">If we cancel your jump, you will receive a 100% refund or can reschedule to any future date at no additional cost.</p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Phone className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Cancellation Support</h2>
                </div>
              </div>
              <p className="text-gray-300 mb-4">Need help with cancellation? Our support team is here to assist you:</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-bold mb-2">Phone Support</h3>
                  <p className="text-gray-300">8506865393</p>
                  <p className="text-gray-400 text-sm">Daily: 9:00 AM - 6:00 PM</p>
                </div>
                <div>
                  <h3 className="text-white font-bold mb-2">Email Support</h3>
                  <p className="text-gray-300">crew.indyabungy@gmail.com</p>
                  <p className="text-gray-400 text-sm">Response within 24 hours</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPage;